import { defaultExtractErrorMessage } from './extract-error-message';

type ParseAsWarning<T> = {
  _tag: 'Warning';
  is: ErrorIs<T>;
  parsed: {
    error: T;
    title: string;
    message: string;
  };
};

type ParseAsError<T> = {
  _tag: 'Error';
  is: ErrorIs<T>;
  parsed: {
    error: T;
    message: string;
  };
};

type ParseAsSwallowedError<T> = {
  _tag: 'SwallowedError';
  is: ErrorIs<T>;
  parsed: {
    error: T;
  };
};

type ParseAs<T> = ParseAsWarning<T> | ParseAsError<T> | ParseAsSwallowedError<T>;
export type ParsedAsWarning<T> = ParsedErrorAs<T, ParseAsWarning<T>>;
export type ParsedAsError<T> = ParsedErrorAs<T, ParseAsError<T>>;
export type ParsedAsSwallowedError<T> = ParsedErrorAs<T, ParseAsSwallowedError<T>>;
export type UnparsedError = { _tag: 'Unparsed'; error: unknown; message: string };

export type ParsedError<T> = ParsedAsWarning<T> | ParsedAsError<T> | ParsedAsSwallowedError<T>;
export type MaybeParsedError<T> = ParsedError<T> | UnparsedError;
type ParseError<T> =
  | ParseErrorAs<T, ParseAsWarning<T>>
  | ParseErrorAs<T, ParseAsError<T>>
  | ParseErrorAs<T, ParseAsSwallowedError<T>>;

type ErrorIs<T> = (error: unknown) => error is T;
type ParsedErrorAs<T, T2 extends ParseAs<T>> = {
  _tag: T2['_tag'];
  parsed: T2['parsed'];
};

type ParseErrorAs<T, T2 extends ParseAs<T>> = (
  error: unknown,
  extractErrorMessage: (error: unknown) => string
) => ParsedErrorAs<T, T2> | UnparsedError;

const tryParsingWith =
  <T>(parseFn: (error: unknown) => error is T, extractErrorMessage: (error: unknown) => string) =>
  (error: unknown) => {
    if (parseFn(error)) {
      return {
        _tag: 'Parsed' as const,
        parsed: error,
      };
    }
    return {
      _tag: 'Unparsed' as const,
      error,
      message: extractErrorMessage(error),
    };
  };

export const asWarning = <T>(warning: {
  /**
   * Identify whether a given error should be processed as a warning
   */
  is: ErrorIs<T>;
  /**
   * Defines how to extract a warning title from a given parsed error.
   */
  extractTitle: (error: T) => string;
  /**
   * Defines how to extract a warning message from a given parsed error.
   */
  extractMessage: (error: T) => string;
}): ParseErrorAs<T, ParseAsWarning<T>> => {
  return (error, extractErrorMessage) => {
    const parsed = tryParsingWith(warning.is, extractErrorMessage)(error);
    if (parsed._tag === 'Unparsed') return parsed;

    return {
      _tag: 'Warning',
      parsed: {
        error: parsed.parsed,
        title: warning.extractTitle(parsed.parsed),
        message: warning.extractMessage(parsed.parsed),
      },
    };
  };
};

export const asError = <T>({
  is,
  extractMessage = defaultExtractErrorMessage,
}: {
  /**
   * Identify whether a given error should be processed as an error
   */
  is: ErrorIs<T>;
  /**
   * Defines how to extract an error message from a given parsed error.
   * falls back to `defaultExtractErrorMessage`
   */
  extractMessage?: (error: T) => string;
}): ParseErrorAs<T, ParseAsError<T>> => {
  return (error, extractErrorMessage) => {
    const parsed = tryParsingWith(is, extractErrorMessage)(error);
    if (parsed._tag === 'Unparsed') return parsed;

    return {
      _tag: 'Error',
      parsed: {
        error: parsed.parsed,
        message: extractMessage(parsed.parsed),
      },
    };
  };
};

export const asSwallowedError = <T>({
  is,
}: {
  /**
   * Identify whether a given error should be swallowed
   */
  is: ErrorIs<T>;
}): ParseErrorAs<T, ParseAsSwallowedError<T>> => {
  return (error, extractErrorMessage) => {
    const parsed = tryParsingWith(is, extractErrorMessage)(error);
    if (parsed._tag === 'Unparsed') return parsed;

    return {
      _tag: 'SwallowedError',
      parsed: {
        error: parsed.parsed,
      },
    };
  };
};

export const parseError = (parseAs: ParseError<any>[]) => {
  return (error: unknown, extractMessage: (error: unknown) => string = defaultExtractErrorMessage) => {
    const parser =
      parseAs.find((parse) => parse(error, extractMessage)._tag !== 'Unparsed') ??
      ((() => ({ _tag: 'Unparsed', error, message: extractMessage(error) })) as ParseErrorAs<any, ParseAs<any>>);

    return parser(error, extractMessage) as MaybeParsedError<any>;
  };
};
